import React, { useMemo } from 'react';
import FleetWidget from 'models/fleet-management/FleetWidget';
import { useTranslation } from 'react-i18next';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import { FleetFieldTypeLookup, getPieWidgetData } from 'utils/fleet-filters';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { PieChart, PieSeriesType, PieValueType } from '@mui/x-charts';
import { MakeOptional } from '@mui/x-charts/internals';
import { createFleetItemTooltipContent } from './FleetChartTooltip';

const CAPTION_HEIGHT = 24;

const PieChartWidget: React.FC<{
  widget: FleetWidget;
  data: any[] | null;
  getStatus: HttpFetchStatus;
  width: number;
  height: number;
  fieldTypes?: FleetFieldTypeLookup;
}> = ({ widget, data, getStatus, width, height, fieldTypes }) => {
  const { t } = useTranslation();

  const pieData = useMemo(
    () => getPieWidgetData(widget, data, fieldTypes),
    [widget, data, fieldTypes],
  );

  const groupMeta = useMemo(
    () =>
      pieData.map((slice) => ({
        parts: slice.parts ?? [],
        rowCount: slice.rowCount ?? 0,
      })),
    [pieData],
  );
  const itemContent = useMemo(() => createFleetItemTooltipContent(groupMeta), [groupMeta]);

  if (
    widget.fields == null ||
    widget.fields.length !== 1 ||
    widget.groups == null ||
    widget.groups.length < 1
  ) {
    return <CustomAlert header={t('error-chart-incorrectly-configured')} severity="error" />;
  }

  const chartData: MakeOptional<PieSeriesType<MakeOptional<PieValueType, 'id'>>, 'type'>[] = [
    {
      data: pieData,
      innerRadius: 64,
      paddingAngle: 2,
      cornerRadius: 16,
      startAngle: 0,
      endAngle: 360,
    },
  ];

  return (
    <React.Fragment>
      <Stack
        sx={{ height: '100%', width: '100%', overflow: 'hidden' }}
        alignItems="center"
        textAlign="center"
      >
        <PieChart
          loading={getStatus === HttpFetchStatus.Loading}
          series={chartData}
          margin={{ right: 0 }}
          width={width}
          height={Math.max(height - CAPTION_HEIGHT, 0)}
          slotProps={{ legend: { hidden: true } }}
          tooltip={{ trigger: 'item', slots: { itemContent } }}
        />
        <Typography sx={{ width: '100%', textAlign: 'center' }}>
          {t('fleet-management-field-' + widget.fields[0].name)}
        </Typography>
      </Stack>
    </React.Fragment>
  );
};

export default PieChartWidget;
